import { Check, ChevronDown } from 'lucide-react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../cn.js';
import { useUiLabels } from '../labels.js';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  /** Shown before the label, in the list and in the button. */
  icon?: ReactNode;
  /** Second line in the list. */
  description?: string;
  disabled?: boolean;
}

export interface SelectProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  disabled?: boolean;
  /** `default`: like an input. `subtle`: filled, for toolbars. `ghost`: borderless, for property rows. */
  variant?: 'default' | 'subtle' | 'ghost';
  size?: 'sm' | 'md';
  /** Overrides the selected option's icon in the button. */
  icon?: ReactNode;
  placeholder?: string;
  className?: string;
}

const triggerVariants = {
  default:
    'border border-border bg-surface shadow-panel hover:border-border-strong focus-visible:border-accent focus-visible:ring-4 focus-visible:ring-accent/15 aria-[invalid=true]:border-danger',
  subtle:
    'bg-surface-2 ring-1 ring-inset ring-border hover:bg-surface-3/60 focus-visible:ring-accent',
  ghost: 'font-medium hover:bg-surface-2 focus-visible:bg-surface-2 aria-expanded:bg-surface-2',
};

const LIST_MAX_HEIGHT = 288;

/** Runs a DOM call that older engines (and jsdom) may lack; without it the list renders in place. */
function tryDom<T>(call: () => T, fallback: T): T {
  try {
    return call();
  } catch {
    return fallback;
  }
}
const isPopoverOpen = (el: HTMLElement) => tryDom(() => el.matches(':popover-open'), false);
const GAP = 4;

/**
 * Themed dropdown (the native <select> list ignores the app's theme). Select-only combobox from
 * the WAI-ARIA practices: focus stays on the button, arrows move, Enter/Space pick, Esc closes and
 * typing jumps to an option. The list is a manual popover (top layer), so it is never clipped and
 * still works inside modal <dialog>s.
 */
export function Select<T extends string = string>({
  value,
  onChange,
  options,
  id,
  disabled,
  variant = 'default',
  size = 'md',
  icon,
  placeholder,
  className,
  ...aria
}: SelectProps<T>) {
  const labels = useUiLabels();
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>();
  const typed = useRef({ text: '', at: 0 });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = options[selectedIndex];

  const enabledFrom = (start: number, step: 1 | -1) => {
    for (let i = start; i >= 0 && i < options.length; i += step) {
      if (!options[i]?.disabled) return i;
    }
    return -1;
  };

  function show() {
    if (disabled) return;
    setActive(selectedIndex >= 0 ? selectedIndex : enabledFrom(0, 1));
    setOpen(true);
  }

  function hide(refocus = true) {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }

  function pick(index: number) {
    const option = options[index];
    if (!option || option.disabled) return;
    if (option.value !== value) onChange(option.value);
    hide();
  }

  // Place the list under the button (above it when there is no room) and keep it there.
  useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const height = Math.min(list?.scrollHeight ?? LIST_MAX_HEIGHT, LIST_MAX_HEIGHT);
      const below = window.innerHeight - rect.bottom;
      const top =
        below < height + GAP && rect.top > below
          ? Math.max(GAP, rect.top - height - GAP)
          : rect.bottom + GAP;
      const width = Math.max(rect.width, 176);
      const left = Math.min(rect.left, window.innerWidth - width - GAP);
      setPosition({ top, left: Math.max(GAP, left), width });
    };
    if (list && !isPopoverOpen(list)) tryDom(() => list.showPopover(), undefined);
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      if (list && isPopoverOpen(list)) tryDom(() => list.hidePopover(), undefined);
    };
  }, [open]);

  // Close on a click anywhere else.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) hide(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  // Keep the highlighted option visible.
  useEffect(() => {
    if (!open || active < 0) return;
    const option = listRef.current?.querySelector(`[data-index="${active}"]`);
    if (option) tryDom(() => option.scrollIntoView({ block: 'nearest' }), undefined);
  }, [open, active]);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    const key = event.key;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key)) {
        event.preventDefault();
        show();
      }
      return;
    }
    if (key === 'Escape') {
      // Close only the list, not the dialog around it.
      event.preventDefault();
      event.stopPropagation();
      hide();
    } else if (key === 'Tab') {
      hide(false);
    } else if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      const step = key === 'ArrowDown' ? 1 : -1;
      const next = enabledFrom(active + step, step);
      if (next >= 0) setActive(next);
    } else if (key === 'Home' || key === 'End') {
      event.preventDefault();
      setActive(key === 'Home' ? enabledFrom(0, 1) : enabledFrom(options.length - 1, -1));
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      pick(active);
    } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      // Type-ahead: jump to the next option starting with what was typed.
      const now = Date.now();
      typed.current = {
        text: (now - typed.current.at < 600 ? typed.current.text : '') + key.toLowerCase(),
        at: now,
      };
      const text = typed.current.text;
      const order = [...options.keys()].map(
        (i) => (i + active + (text.length === 1 ? 1 : 0)) % options.length,
      );
      const match = order.find(
        (i) => !options[i]?.disabled && options[i]?.label.toLowerCase().startsWith(text),
      );
      if (match !== undefined) setActive(match);
    }
  }

  const shownIcon = icon ?? selected?.icon;

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={aria['aria-label']}
        aria-invalid={aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        disabled={disabled}
        onClick={() => (open ? hide() : show())}
        onKeyDown={onKeyDown}
        className={cn(
          'inline-flex min-w-0 items-center gap-2 rounded-lg text-left text-sm text-text transition-[background-color,border-color,box-shadow] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
          size === 'sm' ? 'h-8 px-2.5 text-[13px]' : 'h-9 px-3',
          triggerVariants[variant],
          variant === 'ghost' ? 'max-w-full px-2' : 'w-full',
          className,
        )}
      >
        {shownIcon && (
          <span className="flex shrink-0 items-center [&_svg]:size-4">{shownIcon}</span>
        )}
        <span
          className={cn(
            'min-w-0 truncate',
            variant !== 'ghost' && 'flex-1',
            !selected && 'text-subtle',
          )}
        >
          {selected?.label ?? placeholder ?? labels.choose}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            'size-4 shrink-0 text-subtle transition-transform',
            open && 'rotate-180',
            variant === 'ghost' && 'text-subtle/60',
          )}
        />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          popover="manual"
          aria-label={aria['aria-label']}
          style={position && { top: position.top, left: position.left, minWidth: position.width }}
          className={cn(
            'scrollbar-thin fixed inset-auto m-0 max-h-72 max-w-[calc(100vw-1rem)] animate-fade-in overflow-y-auto rounded-xl border border-border bg-surface p-1 text-sm text-text shadow-float',
            !position && 'invisible',
          )}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value;
            return (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                data-index={index}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                onPointerMove={() => !option.disabled && setActive(index)}
                onClick={() => pick(index)}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5',
                  index === active && 'bg-surface-2',
                  isSelected && 'font-medium text-accent',
                  option.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {option.icon && (
                  <span className="flex shrink-0 items-center [&_svg]:size-4">{option.icon}</span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block truncate text-xs font-normal text-muted">
                      {option.description}
                    </span>
                  )}
                </span>
                <Check
                  aria-hidden
                  className={cn('size-4 shrink-0', isSelected ? 'text-accent' : 'invisible')}
                />
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
